import { useSize } from "../../lib/useSize";
import { fmtKpi, chartColor, type ChartPoint } from "../../lib/dashboard";

function arcPath(cx: number, cy: number, rO: number, rI: number, a0: number, a1: number): string {
  const pt = (a: number, r: number) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = pt(a0, rO), [x1, y1] = pt(a1, rO), [x2, y2] = pt(a1, rI), [x3, y3] = pt(a0, rI);
  return `M ${x0} ${y0} A ${rO} ${rO} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${rI} ${rI} 0 ${large} 0 ${x3} ${y3} Z`;
}

/** Kütüphanesiz halka (donut) grafik + lejant — dilim başına --chart-N. groupBy kategorileri (vardiya/gün) paylar. */
export function DonutChart({ data, unit }: { data: ChartPoint[]; unit?: string | null }) {
  const [ref, { width, height }] = useSize<HTMLDivElement>();
  const slices = data.map((d, i) => ({ label: d.label, v: Math.max(0, d.value ?? 0), color: chartColor(i) })).filter((s) => s.v > 0);
  const total = slices.reduce((a, s) => a + s.v, 0);
  const showLegend = width >= 220;
  const chartW = showLegend ? Math.min(width * 0.5, height + 20) : width;
  const d = Math.max(0, Math.min(chartW, height) - 10);
  const cx = chartW / 2, cy = height / 2, rO = d / 2, rI = rO * 0.58;

  let a = -Math.PI / 2;
  const arcs = slices.map((s) => {
    const a0 = a, a1 = a + (s.v / (total || 1)) * Math.PI * 2;
    a = a1;
    return { s, a0, a1 };
  });

  return (
    <div ref={ref} className="dash-chart dash-chart-donut">
      {width > 8 && height > 8 && total > 0 ? (
        <svg width={width} height={height} role="img" aria-label="Halka grafik">
          {slices.length === 1 ? (
            <g>
              <circle cx={cx} cy={cy} r={(rO + rI) / 2} fill="none" stroke={slices[0]!.color} strokeWidth={rO - rI} />
              <title>{slices[0]!.label}: {fmtKpi(slices[0]!.v)}{unit ? " " + unit : ""}</title>
            </g>
          ) : (
            arcs.map((x, i) => (
              <path key={i} d={arcPath(cx, cy, rO, rI, x.a0, x.a1)} fill={x.s.color}>
                <title>{x.s.label}: {fmtKpi(x.s.v)}{unit ? " " + unit : ""} ({Math.round((x.s.v / total) * 100)}%)</title>
              </path>
            ))
          )}
          {showLegend ? (
            <g transform={`translate(${chartW + 8}, ${Math.max(10, cy - slices.length * 9)})`}>
              {slices.slice(0, 8).map((s, i) => (
                <g key={i} transform={`translate(0, ${i * 18})`}>
                  <rect x={0} y={0} width={10} height={10} rx={2} fill={s.color} />
                  <text x={15} y={9} className="dash-chart-legend">{s.label} · {fmtKpi(s.v)}</text>
                </g>
              ))}
            </g>
          ) : null}
        </svg>
      ) : (
        width > 8 ? <div className="dash-chart-empty">Veri yok</div> : null
      )}
    </div>
  );
}
